import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Lock, Unlock, ArrowRight, Square, ArrowLeft } from "lucide-react";
import { ASSETS } from "../data";
import { Seo, SITE } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Preview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/preview`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const toc = data?.toc || [];
  const sample = data?.sample;

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay text-white">
      <Seo
        title="Look Inside — Ghost The Grid Privacy & Disappearance Pack"
        description="Preview the Ghost The Grid pack: read a free sample chapter and see all 13 nodes before you buy. One-time $29, no subscription."
        path="/preview"
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Privacy & Disappearance Pack",
          description: "A 12-node privacy playbook plus bonus encrypted-email chapter. Preview a free sample chapter before buying.",
          brand: { "@type": "Organization", name: "Ghost The Grid" },
          offers: { "@type": "Offer", price: "29.00", priceCurrency: "USD", availability: "https://schema.org/InStock", url: `${SITE}/#buy` },
        }}
      />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="preview-home" className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="Ghost The Grid" className="h-8 w-8 object-cover rounded-sm" />
            <span className="hidden sm:inline font-display font-extrabold uppercase tracking-tight text-sm">Look Inside</span>
          </Link>
          <a href="/#buy" data-testid="preview-buy-top" className="bg-accent text-white font-mono uppercase tracking-[0.15em] text-[11px] px-5 py-2.5 hover:bg-white hover:text-black transition-colors">
            Get the pack
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-accent" size={28} /></div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors mb-8">
                <ArrowLeft size={14} /> Back to home
              </Link>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-3">// Look inside the pack</p>
              <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6 leading-[0.95]">
                See exactly what you're getting
              </h1>
              <div className="portal-md mb-10">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.intro || ""}</ReactMarkdown>
              </div>
            </motion.div>

            {/* Table of contents */}
            <div className="mb-14">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-5">// {toc.length} chapters inside</p>
              <div className="border-t border-white/10">
                {toc.map((n, i) => {
                  const free = i === 0;
                  return (
                    <div key={n.number} data-testid={`toc-${n.number}`} className="group grid grid-cols-12 items-center gap-3 py-4 border-b border-white/10">
                      <div className="col-span-2 sm:col-span-1 font-mono text-sm text-white/30">{n.number}</div>
                      <div className="col-span-8 sm:col-span-9">
                        <h3 className="font-display font-bold text-base sm:text-lg tracking-tight">{n.title}</h3>
                        <p className="font-body text-xs text-white/40 leading-relaxed line-clamp-1">{n.summary}</p>
                      </div>
                      <div className="col-span-2 flex justify-end">
                        {free ? (
                          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-accent"><Unlock size={12} /> Free</span>
                        ) : (
                          <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30"><Lock size={12} /></span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Free sample node */}
            {sample && (
              <div className="border border-accent/30 bg-[#0a0a0a] p-6 sm:p-8" data-testid="preview-sample">
                <div className="flex items-center gap-2 mb-2">
                  <Unlock size={14} className="text-accent" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">Free sample · Node {sample.number}</span>
                </div>
                <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl mb-5">{sample.title}</h2>
                <div className="portal-md mb-8">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{ a: ({ node: _n, ...p }) => <a {...p} target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2" /> }}
                  >
                    {sample.markdown}
                  </ReactMarkdown>
                </div>
                {(sample.checklist_items || []).length > 0 && (
                  <div className="border border-white/10 bg-black/40 p-5">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-4">Action checklist</span>
                    <ul className="space-y-3">
                      {sample.checklist_items.map((it) => (
                        <li key={it.id} className="flex items-start gap-3">
                          <Square size={18} className="text-white/25 shrink-0 mt-0.5" />
                          <span className="font-body text-sm text-white/70 leading-relaxed">{it.text}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">Tick-off progress saves in your member dashboard after purchase</p>
                  </div>
                )}
              </div>
            )}

            {/* Buy CTA */}
            <div className="mt-12 border border-accent/40 bg-[#160606] p-7 text-center" data-testid="preview-buy-box">
              <h3 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl mb-3">Unlock all {toc.length} chapters</h3>
              <p className="font-body text-white/60 leading-relaxed mb-6 max-w-lg mx-auto">
                One-time payment. No subscription, no data handed over. You get lifetime access to every node, checklist and update in your own member dashboard.
              </p>
              <a href="/#buy" data-testid="preview-buy-cta" className="group inline-flex items-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors">
                Get the full pack <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
